"use client";

import React, { FC, MouseEvent, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import HighQualityIcon from "@mui/icons-material/HighQuality";
import LockIcon from "@mui/icons-material/Lock";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useStyles } from "./styles";
import { applyFirstEditionDefaults } from "utils/cta";

interface GenCardPaywallProps {
  onClose: () => void;
  onUpgrade: () => void;
  ctaContent?: {
    modalTitle?: string;
    modalSubtitle?: string;
    feature1Title?: string;
    feature1Desc?: string;
    feature2Title?: string;
    feature2Desc?: string;
    feature3Title?: string;
    feature3Desc?: string;
  };
  versionIdentifier?: string;
}

const GenCardPaywall: FC<GenCardPaywallProps> = ({
  onClose,
  onUpgrade,
  ctaContent,
  versionIdentifier,
}) => {
  const { classes } = useStyles();
  const modalRef = useRef<HTMLDivElement>(null);
  const spotlightRef = useRef<HTMLDivElement>(null);
  const [particles, setParticles] = useState<
    {
      left: number;
      bottom: number;
      size: number;
      duration: number;
      delay: number;
    }[]
  >([]);
  const router = useRouter();

  useEffect(() => {
    const particleCount = 20;
    const newParticles = [];
    for (let i = 0; i < particleCount; i++) {
      newParticles.push({
        left: Math.random() * 100,
        bottom: -10,
        size: Math.random() * 4 + 2,
        duration: Math.random() * 10 + 10,
        delay: Math.random() * 10,
      });
    }
    setParticles(newParticles);
  }, []);

  useEffect(() => {
    const modalEl = modalRef.current;
    const spotlightEl = spotlightRef.current;
    if (!modalEl || !spotlightEl) return;
    const handleMouseMove = (e: MouseEvent) => {
      const rect = modalEl.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      spotlightEl.style.background = `radial-gradient(circle at ${x}px ${y}px, transparent 30%, rgba(0,0,0,0.05) 100%)`;
    };
    modalEl.addEventListener("mousemove", handleMouseMove as any);
    return () => {
      modalEl.removeEventListener("mousemove", handleMouseMove as any);
    };
  }, []);

  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const defaultsGenCard = {
    modalTitle: "Unlock Premium Content",
    modalSubtitle:
      "Upgrade your account today to access all images and exclusive features!",
    feature1Title: "Full Gallery Access",
    feature1Desc: "View all images without restrictions",
    feature2Title: "HD Resolution",
    feature2Desc: "Enjoy images in maximum quality",
    feature3Title: "Private Creations",
    feature3Desc: "Hide your images from the public gallery",
  };

  const content = applyFirstEditionDefaults(
    versionIdentifier,
    "A",
    defaultsGenCard,
    ctaContent
  );

  return (
    <div className={classes.overlay} onClick={onClose}>
      <div className={classes.modalContainer} onClick={handleContainerClick}>
        <div className={classes.modal} ref={modalRef}>
          <div className={classes.spotlight} ref={spotlightRef} />
          <div className={classes.cornerRibbon}>PREMIUM</div>
          <div className={classes.modalHeader}>
            <div className={classes.blurredBg} />
            <div className={classes.particlesContainer}>
              {particles.map((p, idx) => (
                <div
                  key={idx}
                  className={classes.particle}
                  style={{
                    left: `${p.left}%`,
                    bottom: `${p.bottom}px`,
                    width: `${p.size}px`,
                    height: `${p.size}px`,
                    animationDuration: `${p.duration}s`,
                    animationDelay: `${p.delay}s`,
                  }}
                />
              ))}
            </div>
            <div className={classes.lockContainer}>
              <div className={classes.lockCircle}>
                <LockOutlinedIcon sx={{ fontSize: 36, color: "#ff3e4d" }} />
              </div>
            </div>
          </div>
          <div className={classes.modalBody}>
            <h2 className={classes.modalTitle}>{content.modalTitle}</h2>
            <p className={classes.modalSubtitle}>{content.modalSubtitle}</p>
            <div className={classes.features}>
              <div className={classes.feature}>
                <div className={classes.featureIcon}>
                  <PhotoLibraryIcon fontSize="small" />
                </div>
                <div className={classes.featureContent}>
                  <div className={classes.featureTitle}>
                    {content.feature1Title}
                  </div>
                  <div className={classes.featureDesc}>
                    {content.feature1Desc}
                  </div>
                </div>
              </div>
              <div className={classes.feature}>
                <div className={classes.featureIcon}>
                  <HighQualityIcon fontSize="small" />
                </div>
                <div className={classes.featureContent}>
                  <div className={classes.featureTitle}>
                    {content.feature2Title}
                  </div>
                  <div className={classes.featureDesc}>
                    {content.feature2Desc}
                  </div>
                </div>
              </div>
              <div className={classes.feature}>
                <div className={classes.featureIcon}>
                  <LockIcon fontSize="small" />
                </div>
                <div className={classes.featureContent}>
                  <div className={classes.featureTitle}>
                    {content.feature3Title}
                  </div>
                  <div className={classes.featureDesc}>
                    {content.feature3Desc}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={classes.modalFooter}>
            <button className={classes.btnUpgrade} onClick={onUpgrade}>
              UPGRADE NOW
              <div className={classes.btnRing}></div>
            </button>
            <button className={classes.btnCancel} onClick={onClose}>
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenCardPaywall;
